import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import styles from './ui-tile-buttons.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UITileButtons
 * @element ui-tile-buttons
 * @classdesc `ui-tile-buttons` represents a container for multiple `ui-tile-button` elements (not more than 5). It serves as a navigation landmark for the contained buttons, so it is required to provide an accessible name via `aria-label` attribute on the host element.
 * @slot
 * @example
 * <ui-tile-buttons aria-label="Navigation links">
 *  <ui-tile-button glyph="redesign-atm-in-out" label="Button 1" href="#link-1"></ui-tile-button>
 *  <ui-tile-button glyph="redesign-chat-bubbles" label="Button 2" href="#link-2"></ui-tile-button>
 *  <ui-tile-button glyph="redesign-circle-percent" is-button label="Button 3"></ui-tile-button>
 *  <ui-tile-button glyph="redesign-file-arrow-right" is-button label="An offers button" badge-label="3" aria-label="An offers button, 3 unread"></ui-tile-button>
 * </ui-tile-buttons>
 */

@Component({
    tag: 'ui-tile-buttons',
    styles,
})
export class UITileButtons extends UIElement {
    /**
     * @inheritDoc
     */
    render(): void {
        this.updateElement({
            classList: { 'ui-tile-buttons': true },
        });
        const nav = this.createElement({
            tagName: 'nav',
            classList: {
                'ui-tile-buttons__nav': true,
            },
            children: [
                {
                    tagName: 'div',
                    classList: {
                        'ui-tile-buttons__nav-list': true,
                    },
                    attributes: {
                        role: 'list',
                    },
                    children: this.childNodes,
                },
            ],
        });

        // Move aria-label from host to navigation to provide a label for the landmark
        if (this.hasAttribute('aria-label')) {
            nav.setAttribute('aria-label', this.getAttribute('aria-label')!);
            this.removeAttribute('aria-label');
        }

        this.append(nav);
    }
}
